import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Send, Plus, Trash2, Sparkles, Copy, RefreshCw, Check, X, Menu, LogOut,
  MessageSquareText, Stethoscope, Scale, Receipt, ShieldCheck, FileText, Baby, BadgePercent,
  ChevronRight, Loader2, Eraser, Info, Library, User as UserIcon, Paperclip,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import Markdown from "@/components/Markdown";
import Disclaimer from "@/components/Disclaimer";
import { SourceCardsGrid } from "@/components/SourceCards";
import { useAuth } from "@/context/AuthContext";
import { api, API_BASE, formatApiError } from "@/lib/api";

const SUGGESTIONS = [
  { icon: Stethoscope, q: "Which health insurance is best for a family of four?" },
  { icon: Scale, q: "Compare Star Health and Niva Bupa." },
  { icon: FileText, q: "Explain this policy in simple English." },
  { icon: Baby, q: "Which policy covers maternity?" },
  { icon: ShieldCheck, q: "Best insurance plan for a diabetic aged 40." },
  { icon: Receipt, q: "How much tax can I save through health insurance?" },
  { icon: BadgePercent, q: "Should I buy term insurance or health insurance first?" },
  { icon: MessageSquareText, q: "What is the difference between co-pay, deductible and sub-limit?" },
];

const FOLLOWUP_BY_TOPIC = [
  "Show me a side-by-side comparison",
  "What are the main exclusions to watch for?",
  "How does this affect my tax savings?",
  "Suggest a policy for my family",
];

function UsageBar({ label, used, limit, testid }) {
  const cap = limit ?? 0;
  const u = used ?? 0;
  const pct = cap > 0 ? Math.min(100, Math.round((u / cap) * 100)) : 0;
  const near = pct >= 80;
  const full = pct >= 100;
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between text-[10.5px] font-medium">
        <span className="text-muted-foreground">{label}</span>
        <span className={full ? "text-destructive font-semibold" : near ? "text-amber-600 dark:text-amber-400" : "text-foreground"}>
          {u} / {cap}
        </span>
      </div>
      <div className="mt-1 h-1.5 rounded-full bg-border/60 overflow-hidden">
        <div
          className={
            "h-full transition-all " +
            (full ? "bg-destructive" : near ? "bg-amber-500" : "bg-secondary")
          }
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export default function Chat() {
  const { conversationId: routeId } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, logout } = useAuth();

  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeId, setActiveId] = useState(routeId || null);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [streamingSources, setStreamingSources] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [attachedDocs, setAttachedDocs] = useState([]); // [{document_id, policy_name}]
  const [uploading, setUploading] = useState(false);
  const [usage, setUsage] = useState(null); // {plan, chats_this_month, chats_limit, documents_count, documents_limit, ...}

  const scrollRef = useRef(null);
  const abortRef = useRef(null);
  const lastUserMsgRef = useRef(null);
  const fileInputRef = useRef(null);

  // Load conversations on mount
  const loadConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/conversations");
      setConversations(data);
    } catch (_e) { /* ignore */ }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  const loadUsage = useCallback(async () => {
    try {
      const { data } = await api.get("/plan/usage");
      setUsage(data);
    } catch (_e) { /* ignore */ }
  }, []);

  useEffect(() => { loadUsage(); }, [loadUsage]);

  // Load messages when active conv changes
  useEffect(() => {
    if (!activeId) { setMessages([]); return; }
    (async () => {
      try {
        const { data } = await api.get(`/conversations/${activeId}/messages`);
        setMessages(data);
      } catch (err) {
        toast.error(formatApiError(err));
      }
    })();
  }, [activeId]);

  // Sync route param
  useEffect(() => {
    if (routeId !== activeId) setActiveId(routeId || null);
  }, [routeId, activeId]);

  // Auto-scroll on new content
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, streamingText, streaming]);

  // If a prompt arrived from the landing page, prefill the input
  useEffect(() => {
    const p = params.get("prompt");
    if (p && !input) setInput(p);
    const docId = params.get("doc");
    if (docId) {
      (async () => {
        try {
          const { data } = await api.get(`/documents/${docId}`);
          setAttachedDocs((prev) => {
            if (prev.some((d) => d.document_id === data.document_id)) return prev;
            return [...prev, { document_id: data.document_id, policy_name: data.policy_name }];
          });
        } catch (_e) { /* ignore */ }
      })();
    }
  }, []);

  const sendMessage = async (text) => {
    if (!text.trim() || streaming) return;
    lastUserMsgRef.current = text;

    // Optimistic user bubble
    const tempUser = {
      message_id: `tmp_${Date.now()}`,
      role: "user",
      content: text,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, tempUser]);
    setInput("");
    setStreaming(true);
    setStreamingText("");

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch(`${API_BASE}/chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          conversation_id: activeId,
          message: text,
          document_ids: attachedDocs.map((d) => d.document_id),
        }),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        // 402 → quota exceeded, surface a targeted upgrade prompt
        if (res.status === 402) {
          const body = await res.json().catch(() => ({}));
          const d = body?.detail || {};
          toast.error(d.message || "Free plan limit reached", {
            description: "Upgrade to Pro for unlimited chats.",
            action: { label: "Upgrade", onClick: () => navigate("/pricing") },
            duration: 8000,
          });
          throw new Error(d.message || "Chat limit reached");
        }
        const errText = await res.text().catch(() => "Stream failed");
        throw new Error(errText || `HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let assistantText = "";
      let newConvId = activeId;
      let assistantMsgId = null;
      let sources = [];

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const ev of events) {
          const line = ev.split("\n").find((l) => l.startsWith("data: "));
          if (!line) continue;
          try {
            const payload = JSON.parse(line.slice(6));
            if (payload.type === "delta") {
              assistantText += payload.content;
              setStreamingText(assistantText);
            } else if (payload.type === "sources") {
              sources = payload.sources || [];
              setStreamingSources(sources);
            } else if (payload.type === "done") {
              assistantMsgId = payload.message_id;
              newConvId = payload.conversation_id;
            } else if (payload.type === "error") {
              throw new Error(payload.message || "AI error");
            }
          } catch (_e) { /* skip bad chunk */ }
        }
      }

      // Commit assistant message (with sources attached)
      setMessages((m) => [
        ...m,
        {
          message_id: assistantMsgId || `asst_${Date.now()}`,
          role: "assistant",
          content: assistantText,
          sources,
          created_at: new Date().toISOString(),
        },
      ]);
      setStreamingText("");
      setStreamingSources([]);
      setStreaming(false);

      if (newConvId && newConvId !== activeId) {
        setActiveId(newConvId);
        navigate(`/chat/${newConvId}`, { replace: true });
      }
      loadConversations();
      loadUsage();
    } catch (err) {
      setStreaming(false);
      setStreamingText("");
      setStreamingSources([]);
      if (err.name !== "AbortError") {
        toast.error(err.message || "Something went wrong.");
        // remove the optimistic user message on hard failure
        setMessages((m) => m.filter((x) => x.message_id !== tempUser.message_id));
      }
    } finally {
      abortRef.current = null;
    }
  };

  const stopStream = () => {
    abortRef.current?.abort();
    setStreaming(false);
  };

  const ACCEPTED_EXT = [".pdf", ".docx", ".png", ".jpg", ".jpeg", ".webp"];
  const ACCEPT_ATTR = "application/pdf,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx,image/png,image/jpeg,image/webp";

  const handleAttachClick = () => {
    if (uploading || streaming) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file later
    if (!file) return;

    const name = file.name || "";
    const ext = "." + name.split(".").pop().toLowerCase();
    if (!ACCEPTED_EXT.includes(ext)) {
      toast.error("Unsupported file. Allowed: PDF, DOCX, PNG, JPG, JPEG, WEBP.");
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File too large (max 15 MB).");
      return;
    }

    setUploading(true);
    const tid = toast.loading(`Uploading ${name}…`);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("policy_name", name.replace(/\.[^.]+$/, ""));
      form.append("document_type", "user-upload");
      form.append("category", "other");
      const { data } = await api.post("/documents/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachedDocs((prev) => {
        if (prev.some((d) => d.document_id === data.document_id)) return prev;
        return [...prev, { document_id: data.document_id, policy_name: data.policy_name || name }];
      });
      toast.success(`${name} attached. InsureGenie is analyzing it…`, { id: tid });
      loadUsage();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402) {
        const d = err?.response?.data?.detail || {};
        toast.error(d.message || "Free upload limit reached", {
          id: tid,
          description: "Upgrade to Pro for unlimited uploads.",
          action: { label: "Upgrade", onClick: () => navigate("/pricing") },
          duration: 8000,
        });
      } else {
        toast.error(formatApiError(err) || "Upload failed", { id: tid });
      }
    } finally {
      setUploading(false);
    }
  };

  const regenerate = () => {
    if (!lastUserMsgRef.current || streaming) return;
    // Drop last assistant message and re-send the last user message
    setMessages((m) => {
      const copy = [...m];
      if (copy.length && copy[copy.length - 1].role === "assistant") copy.pop();
      // Also drop the trailing user msg so sendMessage re-adds it
      if (copy.length && copy[copy.length - 1].role === "user") copy.pop();
      return copy;
    });
    sendMessage(lastUserMsgRef.current);
  };

  const copyMessage = (id, text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1500);
    });
  };

  const newChat = () => {
    setActiveId(null);
    setMessages([]);
    setStreamingText("");
    navigate("/chat");
    setMobileSidebarOpen(false);
  };

  const openConversation = (id) => {
    setActiveId(id);
    navigate(`/chat/${id}`);
    setMobileSidebarOpen(false);
  };

  const deleteConversation = async (id) => {
    try {
      await api.delete(`/conversations/${id}`);
      toast.success("Conversation deleted");
      if (activeId === id) newChat();
      loadConversations();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const clearCurrent = async () => {
    if (!activeId) { newChat(); return; }
    await deleteConversation(activeId);
  };

  const initials = useMemo(() => {
    const n = user?.name || user?.email || "U";
    return n.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();
  }, [user]);

  const Sidebar = (
    <aside className="h-full w-full flex flex-col bg-card border-r border-border">
      <div className="p-4 border-b border-border flex items-center justify-between">
        <Logo />
        <ThemeToggle />
      </div>
      <div className="p-3 space-y-1">
        <Button
          data-testid="sidebar-new-chat-btn"
          onClick={newChat}
          className="w-full justify-start rounded-xl h-10 bg-primary hover:bg-primary/90 text-primary-foreground"
        >
          <Plus className="w-4 h-4 mr-2" /> New chat
        </Button>
        <Link
          data-testid="sidebar-documents-link"
          to="/documents"
          className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <FileText className="w-4 h-4" /> Documents
        </Link>
        <Link
          data-testid="sidebar-profile-link"
          to="/profile"
          className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <UserIcon className="w-4 h-4" /> Profile
        </Link>
      </div>

      {usage && (
        <div className="px-3 pb-3" data-testid="usage-panel">
          {usage.plan === "pro" ? (
            <div className="rounded-lg border border-secondary/30 bg-secondary/[0.06] px-3 py-2 text-[11px]">
              <div className="flex items-center gap-1.5 font-semibold text-secondary">
                <Sparkles className="w-3 h-3" /> PRO — Unlimited
              </div>
              <div className="mt-0.5 text-muted-foreground">
                {usage.plan_expires_at
                  ? `Renews ${new Date(usage.plan_expires_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}`
                  : "Active"}
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-border bg-muted/30 px-3 py-2.5 space-y-2">
              <UsageBar
                label="Chats"
                used={usage.chats_this_month}
                limit={usage.chats_limit}
                testid="usage-chats"
              />
              <UsageBar
                label="Documents"
                used={usage.documents_count}
                limit={usage.documents_limit}
                testid="usage-docs"
              />
              <Link
                data-testid="sidebar-upgrade-btn"
                to="/pricing"
                className="mt-1 flex items-center justify-center gap-1 h-7 rounded-md bg-secondary text-secondary-foreground text-[11px] font-semibold hover:bg-secondary/90"
              >
                <Sparkles className="w-3 h-3" /> Upgrade to Pro
              </Link>
            </div>
          )}
        </div>
      )}
      <div className="px-3 pb-2">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground px-1">Conversations</p>
      </div>
      <ScrollArea className="flex-1 px-2 scrollbar-thin">
        <div className="space-y-1 pb-2">
          {conversations.length === 0 && (
            <div className="text-[12px] text-muted-foreground px-3 py-6 text-center leading-relaxed">
              Your past conversations will appear here.
            </div>
          )}
          {conversations.map((c) => (
            <div
              key={c.conversation_id}
              data-testid={`conv-item-${c.conversation_id}`}
              className={`group flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer text-sm transition-colors ${
                activeId === c.conversation_id ? "bg-accent/10 text-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
              onClick={() => openConversation(c.conversation_id)}
            >
              <MessageSquareText className={`w-4 h-4 flex-shrink-0 ${activeId === c.conversation_id ? "text-accent" : ""}`} />
              <span className="flex-1 truncate">{c.title || "New conversation"}</span>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    data-testid={`conv-delete-${c.conversation_id}`}
                    onClick={(e) => e.stopPropagation()}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this conversation?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently remove the conversation and all its messages.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      data-testid="delete-confirm-btn"
                      onClick={() => deleteConversation(c.conversation_id)}
                      className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ))}
        </div>
      </ScrollArea>

      <div className="p-3 border-t border-border">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="user-menu-trigger"
              className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-muted transition-colors"
            >
              <Avatar className="w-8 h-8">
                {user?.picture ? <AvatarImage src={user.picture} alt={user.name} /> : null}
                <AvatarFallback className="bg-secondary text-secondary-foreground text-xs font-semibold">{initials}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0 text-left">
                <div className="text-sm font-medium truncate">{user?.name || user?.email}</div>
                <div className="text-[11px] text-muted-foreground truncate">{user?.email}</div>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="text-xs text-muted-foreground">Account</DropdownMenuLabel>
            <DropdownMenuItem asChild>
              <Link data-testid="menu-profile-link" to="/profile"><UserIcon className="w-3.5 h-3.5 mr-2" /> Profile</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link data-testid="menu-documents-link" to="/documents"><FileText className="w-3.5 h-3.5 mr-2" /> Documents</Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link data-testid="menu-home-link" to="/">← Back to home</Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              data-testid="menu-logout-btn"
              onClick={async () => { await logout(); navigate("/"); }}
              className="text-destructive focus:text-destructive"
            >
              <LogOut className="w-3.5 h-3.5 mr-2" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );

  return (
    <TooltipProvider>
      <div data-testid="chat-page" className="h-screen w-screen bg-background text-foreground flex overflow-hidden">
        {/* Desktop sidebar */}
        <div className="hidden md:flex w-[280px] flex-shrink-0">{Sidebar}</div>

        {/* Mobile sidebar */}
        <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
          <SheetContent side="left" className="p-0 w-[280px]">{Sidebar}</SheetContent>
        </Sheet>

        {/* Main */}
        <main className="flex-1 flex flex-col min-w-0 relative">
          {/* Top bar */}
          <div className="h-14 flex items-center justify-between px-4 border-b border-border bg-background/80 backdrop-blur-xl">
            <div className="flex items-center gap-2">
              <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
                <SheetTrigger asChild>
                  <Button data-testid="mobile-sidebar-trigger" variant="ghost" size="icon" className="md:hidden">
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
              </Sheet>
              <div className="flex items-center gap-2 text-sm">
                <Sparkles className="w-4 h-4 text-accent" />
                <span className="font-display font-semibold tracking-tight">InsureGenie</span>
                <span className="hidden sm:inline text-muted-foreground">·</span>
                <span className="hidden sm:inline text-muted-foreground">Claude Sonnet 4.5</span>
                <span className="hidden md:inline-flex items-center gap-1 text-[11px] font-medium bg-accent/10 text-accent border border-accent/20 rounded-full px-2 py-0.5 ml-1">
                  <span role="img" aria-label="India">🇮🇳</span> for India
                </span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    data-testid="clear-conversation-btn"
                    variant="ghost" size="icon"
                    onClick={clearCurrent}
                    disabled={!messages.length}
                  >
                    <Eraser className="w-4 h-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Clear current conversation</TooltipContent>
              </Tooltip>
            </div>
          </div>

          {/* Messages area */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-thin">
            {messages.length === 0 && !streaming ? (
              <EmptyState onPick={(q) => sendMessage(q)} userName={user?.name?.split(" ")[0]} />
            ) : (
              <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-6 pb-40">
                {messages.map((m) => (
                  <MessageBubble
                    key={m.message_id}
                    msg={m}
                    initials={initials}
                    picture={user?.picture}
                    onCopy={copyMessage}
                    copied={copiedId === m.message_id}
                  />
                ))}
                {streaming && (
                  <StreamingBubble text={streamingText} sources={streamingSources} />
                )}
                {!streaming && messages.length > 0 && messages[messages.length - 1].role === "assistant" && (
                  <FollowupRow onPick={(q) => sendMessage(q)} onRegenerate={regenerate} />
                )}
              </div>
            )}
          </div>

          {/* Input */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-background via-background to-transparent pt-6 pb-4 px-4">
            <div className="max-w-3xl mx-auto">
              {attachedDocs.length > 0 && (
                <div data-testid="attached-docs-row" className="mb-2 flex flex-wrap gap-2">
                  {attachedDocs.map((d) => (
                    <span
                      key={d.document_id}
                      data-testid={`attached-doc-${d.document_id}`}
                      className="inline-flex items-center gap-2 bg-accent/10 text-foreground border border-accent/30 rounded-full px-3 py-1 text-[12px]"
                    >
                      <Paperclip className="w-3 h-3 text-accent" />
                      <span className="truncate max-w-[200px]">{d.policy_name}</span>
                      <button
                        onClick={() => setAttachedDocs((p) => p.filter((x) => x.document_id !== d.document_id))}
                        className="hover:text-destructive"
                        aria-label="Detach document"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <form
                onSubmit={(e) => { e.preventDefault(); sendMessage(input); }}
                className="relative bg-card border border-border rounded-2xl shadow-lg focus-within:border-secondary focus-within:ring-4 focus-within:ring-secondary/10 transition-all"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT_ATTR}
                  onChange={handleFileChange}
                  className="hidden"
                  data-testid="chat-file-input"
                />
                <Textarea
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage(input);
                    }
                  }}
                  placeholder={
                    attachedDocs.length > 0
                      ? "Ask anything about this file…"
                      : "Ask InsureGenie about health, life, term insurance, tax savings…"
                  }
                  rows={1}
                  className="min-h-[52px] max-h-40 resize-none border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 pl-12 pr-14 py-3.5 text-[15px]"
                />
                <div className="absolute left-2 bottom-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        data-testid="chat-attach-btn"
                        type="button"
                        onClick={handleAttachClick}
                        disabled={uploading || streaming}
                        size="icon"
                        variant="ghost"
                        className="w-9 h-9 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-40"
                        aria-label="Attach file"
                      >
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Attach PDF, Word doc or image</TooltipContent>
                  </Tooltip>
                </div>
                <div className="absolute right-2 bottom-2 flex items-center gap-1">
                  {streaming ? (
                    <Button
                      data-testid="chat-stop-btn"
                      type="button" onClick={stopStream}
                      size="icon"
                      className="w-9 h-9 rounded-xl bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Button
                      data-testid="chat-send-btn"
                      type="submit"
                      disabled={!input.trim()}
                      size="icon"
                      className="w-9 h-9 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-40"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </form>
              <div className="mt-2.5 text-[11px] text-muted-foreground text-center flex items-center justify-center gap-1.5">
                <Info className="w-3 h-3" />
                Educational guidance only · Not financial advice · Consult a qualified professional.
              </div>
            </div>
          </div>
        </main>
      </div>
    </TooltipProvider>
  );
}

function EmptyState({ onPick, userName }) {
  return (
    <div data-testid="chat-empty-state" className="h-full min-h-[70vh] flex flex-col items-center justify-center px-4 py-12 text-center">
      <div className="w-14 h-14 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center mb-5 shadow-lg">
        <Sparkles className="w-6 h-6 text-accent" />
      </div>
      <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
        {userName ? `Hello, ${userName}.` : "Hello there."}
      </h1>
      <p className="mt-2 text-muted-foreground max-w-md">
        Ask me anything about health, life, term, or tax-saving insurance. I&apos;ll explain it like a conversation.
      </p>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-3xl w-full">
        {SUGGESTIONS.map((s, i) => (
          <button
            data-testid={`suggestion-${i}`}
            key={i}
            onClick={() => onPick(s.q)}
            className="group flex items-start gap-3 p-4 bg-card border border-border rounded-2xl text-left hover:border-accent/40 hover:shadow-md transition-all duration-300"
          >
            <span className="w-9 h-9 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center group-hover:bg-accent/15 group-hover:text-accent transition-colors flex-shrink-0">
              <s.icon className="w-4 h-4" />
            </span>
            <span className="text-sm text-foreground leading-snug">{s.q}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ msg, initials, picture, onCopy, copied }) {
  const isUser = msg.role === "user";
  if (isUser) {
    return (
      <div data-testid={`msg-user-${msg.message_id}`} className="flex justify-end animate-fade-up">
        <div className="bg-primary text-primary-foreground rounded-2xl rounded-tr-md px-4 py-2.5 max-w-[85%] shadow-sm">
          <div className="text-[15px] whitespace-pre-wrap leading-relaxed">{msg.content}</div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`msg-ai-${msg.message_id}`} className="flex gap-3 animate-fade-up">
      <div className="w-8 h-8 rounded-xl bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm">
        <Sparkles className="w-4 h-4 text-accent" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="bg-card border border-border rounded-2xl rounded-tl-md px-5 py-4 shadow-sm">
          <Markdown text={msg.content} />
          {msg.sources && msg.sources.length > 0 && (
            <SourceCardsGrid sources={msg.sources} />
          )}
          <div className="mt-3 pt-3 border-t border-border/60 flex items-center justify-between text-[11px] text-muted-foreground">
            <div className="inline-flex items-center gap-1.5">
              <Library className="w-3 h-3" />
              {msg.sources?.length ? "Cited from your uploaded policies" : "Source: IRDAI guidelines & published policy brochures"}
            </div>
            <div className="flex items-center gap-1">
              <Button
                data-testid={`msg-copy-${msg.message_id}`}
                size="icon" variant="ghost" className="h-7 w-7"
                onClick={() => onCopy(msg.message_id, msg.content)}
                title="Copy"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-accent" /> : <Copy className="w-3.5 h-3.5" />}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StreamingBubble({ text, sources = [] }) {
  return (
    <div data-testid="streaming-bubble" className="flex gap-3">
      <div className="w-8 h-8 rounded-xl bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm">
        <Sparkles className="w-4 h-4 text-accent" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="bg-card border border-border rounded-2xl rounded-tl-md px-5 py-4 shadow-sm">
          {sources.length > 0 && !text && (
            <div data-testid="streaming-sources" className="mb-3 pb-3 border-b border-border/60">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-accent mb-2">Consulting {sources.length} source{sources.length > 1 ? "s" : ""}…</div>
              <div className="flex flex-wrap gap-1.5">
                {sources.slice(0, 6).map((s, i) => (
                  <span key={`${s.source_url || s.document_id || i}-${i}`} className="inline-flex items-center gap-1.5 bg-accent/10 border border-accent/20 rounded-full px-2.5 py-1 text-[11px]">
                    <FileText className="w-3 h-3 text-accent" />
                    <span className="truncate max-w-[180px]">{s.policy_name || s.doc_title || s.insurer_name}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
          {text ? (
            <>
              <Markdown text={text} />
              <span className="inline-block w-1.5 h-4 bg-accent ml-0.5 animate-blink align-middle" />
              {sources.length > 0 && <SourceCardsGrid sources={sources} />}
            </>
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <span className="typing-dot bg-muted-foreground/70" />
                <span className="typing-dot bg-muted-foreground/70" />
                <span className="typing-dot bg-muted-foreground/70" />
              </span>
              <span>{sources.length ? "InsureGenie is reasoning over your documents…" : "InsureGenie is searching your library…"}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FollowupRow({ onPick, onRegenerate }) {
  return (
    <div data-testid="followup-row" className="flex flex-wrap gap-2 pl-11">
      <Button
        data-testid="regenerate-btn"
        size="sm" variant="outline"
        onClick={onRegenerate}
        className="rounded-full h-8 text-xs border-border hover:border-accent hover:text-accent"
      >
        <RefreshCw className="w-3 h-3 mr-1.5" /> Regenerate
      </Button>
      {FOLLOWUP_BY_TOPIC.map((q, i) => (
        <button
          key={i}
          data-testid={`followup-${i}`}
          onClick={() => onPick(q)}
          className="text-xs text-muted-foreground bg-card border border-border rounded-full px-3 py-1.5 hover:border-accent hover:text-foreground transition-all"
        >
          {q}
        </button>
      ))}
    </div>
  );
}
